import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Hero from "@/components/sections/Hero";
import TradesMarquee from "@/components/sections/TradesMarquee";
import GrowthSystem from "@/components/sections/GrowthSystem";
import PlanCard from "@/components/sections/PlanCard";
import CTASection from "@/components/sections/CTASection";
import FAQAccordion from "@/components/sections/FAQAccordion";
import IndustryCard from "@/components/sections/IndustryCard";
import { Reveal, StaggerGroup, staggerItem } from "@/components/motion/Reveal";
import { motion } from "framer-motion";
import { Btn, Eyebrow, SectionHeading } from "@/components/primitives";
import PilotCard from "@/components/sections/PilotCard";
import { plans, problems, processSteps, whyPoints, changes, brand } from "@/content/site";
import { industries } from "@/content/industries";
import { faqs } from "@/content/faqs";
import { AlertCircle, Check } from "lucide-react";

export default function Home() {
  const homeFaqs = faqs.slice(0, 6);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: brand.name,
    description:
      "Green Circle Group helps Australian trade businesses get found, generate more enquiries and turn more opportunities into booked work through managed digital growth subscriptions.",
    url: brand.siteUrl,
    email: brand.email,
    areaServed: "AU",
    logo: brand.logo,
  };

  return (
    <>
      <SEO
        title="Digital Growth for Australian Trades | Green Circle Group"
        description="Green Circle Group helps Australian trade businesses get found, generate more enquiries and turn more opportunities into booked work through managed digital growth subscriptions."
        path="/"
        jsonLd={jsonLd}
      />

      <Hero />
      <TradesMarquee />

      {/* Section 2: Problem recognition */}
      <section
        data-testid="problem-section"
        className="bg-soft py-24 md:py-32"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Sound familiar?</Eyebrow>
            <SectionHeading className="mt-5">
              Too busy working in the business to keep working on its growth?
            </SectionHeading>
            <div className="mt-6 space-y-4 text-lg text-mid-neutral leading-relaxed">
              <p>
                Most trade business owners do not have a shortage of
                responsibility. They are quoting, managing jobs, solving
                problems, speaking with customers and supporting their team.
              </p>
              <p>
                Marketing gets postponed. Missed calls go unanswered. Reviews
                are requested occasionally. Old enquiries disappear into
                messages and spreadsheets. Growth depends on whatever the owner
                has time to do that week.
              </p>
              <p className="text-forest font-semibold">
                Green Circle Group puts a consistent system around the work that keeps
                your pipeline moving.
              </p>
            </div>
          </div>

          <StaggerGroup className="mt-14 grid sm:grid-cols-2 gap-5">
            {problems.map((p) => (
              <motion.div
                key={p.title}
                variants={staggerItem}
                className="rounded-[1.75rem] bg-white border border-border-neutral p-7 md:p-8"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-soft text-mint">
                  <AlertCircle className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-xl font-extrabold text-forest">
                  {p.title}
                </h3>
                <p className="mt-2 text-mid-neutral leading-relaxed">
                  {p.desc}
                </p>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Section 3: Growth system */}
      <GrowthSystem />

      {/* Section 4: Plans preview */}
      <section data-testid="plans-preview" className="bg-soft py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Subscription Plans</Eyebrow>
            <SectionHeading className="mt-5">
              Choose how much of your growth system you want us to manage.
            </SectionHeading>
            <p className="mt-6 text-lg text-mid-neutral leading-relaxed">
              Start with a stronger local foundation, add a managed lead engine
              or put the complete enquiry-to-booking journey on cruise control.
            </p>
          </div>

          <div className="mt-16 grid lg:grid-cols-3 gap-6 lg:gap-5 items-start">
            {plans.map((plan) => (
              <Reveal key={plan.id} delay={plan.recommended ? 0 : 0.08}>
                <PlanCard plan={plan} showAll={false} />
              </Reveal>
            ))}
          </div>

          <div className="mt-12 rounded-[1.75rem] bg-white border border-border-neutral p-7 md:p-9 text-center max-w-3xl mx-auto">
            <p className="text-mid-neutral leading-relaxed">
              Every plan begins with a Growth Assessment so we can understand
              your market, current systems and capacity before recommending the
              right level of support.
            </p>
            <div className="mt-6">
              <Btn to="/plans" variant="dark" withArrow data-testid="compare-plans-btn">
                Compare All Plans
              </Btn>
            </div>
          </div>
        </div>
      </section>

      {/* Specialist Pilots — Energy + Installer */}
      <section
        data-testid="specialist-pilots-section"
        className="bg-white py-24 md:py-32"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Specialist pilots for solar &amp; home energy</Eyebrow>
            <SectionHeading className="mt-5">
              Start with a controlled pilot before committing to an ongoing growth model.
            </SectionHeading>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            <Reveal>
              <PilotCard
                label="Energy Growth Pilot"
                title="Prove campaign quality before scaling"
                description="For solar, battery and home-energy retailers that want to test campaign and lead quality before moving into an ongoing managed lead program."
                ctaLabel="Explore Energy Growth Pilot"
                ctaTo="/energy-growth-pilot"
                testId="home-pilot-egp"
              />
            </Reveal>
            <Reveal delay={0.08}>
              <PilotCard
                label="Installer Growth Pilot"
                title="Build a direct pipeline under your own brand"
                description="For qualified solar and battery installers ready to build a direct pipeline under their own brand, with Green Circle managing the campaign, sales follow-up and agreed installation-readiness administration."
                ctaLabel="Explore Installer Growth Pilot"
                ctaTo="/for/installer-growth-pilot"
                variant="dark"
                testId="home-pilot-igp"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Section 5: How cruise control works — numbered manifesto */}
      <section
        data-testid="cruise-steps-section"
        className="bg-white py-24 md:py-32"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>How Cruise Control Works</Eyebrow>
            <SectionHeading className="mt-5">
              A growth system running in the background—not another job on your
              list.
            </SectionHeading>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {processSteps.map((s, i) => (
              <Reveal
                key={s.step}
                delay={i * 0.06}
                className="rounded-[1.5rem] border border-border-neutral p-6 bg-soft/60"
              >
                <span className="text-4xl font-extrabold text-mint/40">
                  {s.step}
                </span>
                <h3 className="mt-3 text-lg font-extrabold text-forest">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-mid-neutral leading-relaxed">
                  {s.desc}
                </p>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-10 rounded-[1.75rem] bg-forest text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <p className="text-lg text-white/85 leading-relaxed max-w-3xl">
              Cruise control does not mean losing control of your business. You
              remain in the driver&apos;s seat. Green Circle Group keeps the agreed growth
              activity moving quietly in the background and gives you clearer
              visibility of what is happening.
            </p>
            <Btn to="/how-it-works" variant="primary" withArrow className="shrink-0">
              See How It Works
            </Btn>
          </Reveal>
        </div>
      </section>

      {/* Section 6: Industries */}
      <section data-testid="industries-preview" className="bg-soft py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Industries</Eyebrow>
            <SectionHeading className="mt-5">
              Built around the way trade businesses actually work.
            </SectionHeading>
            <p className="mt-6 text-lg text-mid-neutral leading-relaxed">
              Different trades have different services, job values, sales cycles
              and customer behaviour. Green Circle Group adapts the strategy, channels
              and follow-up process to the reality of your business.
            </p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {industries.map((ind, i) => (
              <Reveal key={ind.slug} delay={(i % 3) * 0.06}>
                <IndustryCard industry={ind} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7: Why Green Circle Group */}
      <section data-testid="why-section" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Why Green Circle Group</Eyebrow>
            <SectionHeading className="mt-5">
              More than marketing activity. A system for business growth.
            </SectionHeading>
          </div>
          <StaggerGroup className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyPoints.map((w) => (
              <motion.div
                key={w.title}
                variants={staggerItem}
                className="rounded-[1.5rem] border border-border-neutral p-7 hover:border-mint transition-colors"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-forest text-lime">
                  <Check className="h-5 w-5" strokeWidth={3} />
                </div>
                <h3 className="mt-5 text-xl font-extrabold text-forest">
                  {w.title}
                </h3>
                <p className="mt-2 text-mid-neutral leading-relaxed">
                  {w.desc}
                </p>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Section 8: Results / what we help change */}
      <section data-testid="results-preview" className="bg-forest py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow dark>Results Framework</Eyebrow>
            <SectionHeading dark className="mt-5">
              What we help trade businesses change
            </SectionHeading>
          </div>
          <StaggerGroup className="mt-14 grid md:grid-cols-2 gap-4">
            {changes.map((c) => (
              <motion.div
                key={c}
                variants={staggerItem}
                className="flex items-start gap-4 rounded-2xl bg-white/5 border border-white/10 p-6"
              >
                <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-lime text-forest">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <p className="text-white/85 leading-relaxed">{c}</p>
              </motion.div>
            ))}
          </StaggerGroup>
          <Reveal className="mt-10">
            <Link
              to="/results"
              className="inline-flex items-center gap-2 text-mint font-semibold hover:text-lime transition-colors"
              data-testid="results-link"
            >
              See how we measure progress →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Section 9: FAQ preview */}
      <section data-testid="faq-preview" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <div className="text-center">
            <Eyebrow className="justify-center">Questions</Eyebrow>
            <SectionHeading className="mt-5">
              The things busy owners ask us first
            </SectionHeading>
          </div>
          <div className="mt-12">
            <FAQAccordion items={homeFaqs} emitSchema />
          </div>
          <div className="mt-10 text-center">
            <Btn to="/how-it-works" variant="secondary" withArrow>
              See all questions
            </Btn>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Free Growth Assessment"
        heading="What would change if your growth kept moving quietly in the background?"
        copy="Book a free initial Growth Assessment. Tell us how your business operates today, where you want it to go and what keeps getting in the way."
      />
    </>
  );
}
